import React, { useState, useEffect } from 'react';
import { 
  Check, Sparkles, ShieldCheck, HelpCircle, ArrowRight, Zap, 
  Crown, ChevronDown, ChevronUp, Globe, Calculator, Users, 
  Building, Stethoscope, HeartHandshake, Laptop, Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  CURRENCIES, 
  TIER_PRICING, 
  CurrencyCode, 
  detectDefaultCurrency, 
  formatPriceString 
} from '../utils/currencyDetector';

interface PricingPageProps {
  onSelectPlan?: (tierId: string, billingCycle: 'monthly' | 'annual', currency: CurrencyCode) => void;
  onRequestDistrictDemo?: () => void;
  onNavigateToTab?: (tab: string) => void;
}

export const PricingPage: React.FC<PricingPageProps> = ({
  onSelectPlan,
  onRequestDistrictDemo,
  onNavigateToTab
}) => {
  const { t, i18n } = useTranslation();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [currency, setCurrency] = useState<CurrencyCode>(() => detectDefaultCurrency(i18n.language));
  const [studentCount, setStudentCount] = useState<number>(50);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Auto-sync currency when language changes, if user hasn't overridden
  useEffect(() => {
    const saved = localStorage.getItem('abilityos_currency') as CurrencyCode | null;
    if (!saved) {
      setCurrency(detectDefaultCurrency(i18n.language));
    }
  }, [i18n.language]);

  const handleCurrencyChange = (newCode: CurrencyCode) => {
    setCurrency(newCode);
    localStorage.setItem('abilityos_currency', newCode);
  };

  const currConfig = CURRENCIES[currency];
  const pricing = TIER_PRICING[currency];

  // TCO Calculations
  // Traditional legacy hardware procurement ($3,200 Dr Speech + $6,500 Audiometer + $15,000 AAC + $3,500 ERP + Maintenance = ~$300/student/yr)
  const legacyAnnualCostPerStudent = currency === 'INR' ? 25000 : 350;
  const legacyTotalCost = studentCount * legacyAnnualCostPerStudent;
  const abilityosTotalCost = pricing.school.annualTotal;
  const totalSavings = Math.max(0, legacyTotalCost - abilityosTotalCost);
  const savingsPercent = Math.min(95, Math.round((totalSavings / legacyTotalCost) * 100));

  const comparisonFeatures = [
    { name: 'Core AAC Symbol Board (500+ Words)', community: true, family: true, clinician: true, school: true },
    { name: 'Multilingual TTS (English, Hindi, Marathi)', community: true, family: true, clinician: true, school: true },
    { name: 'CP Motor Tremor Filter & Switch Dwell', community: true, family: true, clinician: true, school: true },
    { name: 'Offline-First PWA & IndexedDB', community: true, family: true, clinician: true, school: true },
    { name: 'Custom Camera Photo Uploads', community: false, family: true, clinician: true, school: true },
    { name: 'Family Audio Voice Recorder per Card', community: false, family: true, clinician: true, school: true },
    { name: 'Voice Balloon Elevation Pitch Game', community: false, family: true, clinician: true, school: true },
    { name: 'First-Then Visual Schedules & Visual Timer', community: false, family: true, clinician: true, school: true },
    { name: 'Reading Ruler & Bionic Dyslexia Contrast', community: false, family: true, clinician: true, school: true },
    { name: 'Dr. Speech Studio (Dual Real-Time FFT & MPT)', community: false, family: false, clinician: true, school: true },
    { name: 'Diagnostic Pure-Tone Audiometer & Audiogram', community: false, family: false, clinician: true, school: true },
    { name: 'Speech Banana Audiometric Zone Overlay', community: false, family: false, clinician: true, school: true },
    { name: 'Articulation Mirror & Oral-Motor Exercises', community: false, family: false, clinician: true, school: true },
    { name: 'Clinical Caseload Management', community: '1 Learner', family: '3 Learners', clinician: '25 Patients', school: 'Unlimited' },
    { name: 'SOAP Progress Notes & Clinical Audio Copilot', community: false, family: false, clinician: true, school: true },
    { name: '1-Click Official Standardized IEP PDF Exporter', community: false, family: false, clinician: true, school: true },
    { name: 'School ERP, Rosters & Multi-Wing Admin', community: false, family: false, clinician: false, school: true },
    { name: 'NDIS & UDID Electronic Billing Claims', community: false, family: false, clinician: 'Manual', school: 'Automated' },
    { name: 'EVV GPS Geofenced Home Visit Audit Tracker', community: false, family: false, clinician: false, school: true },
    { name: 'Unified Native Fleet Hub (All 31 Modules on Win, Mac, Android & PWA)', community: 'Web PWA Only', family: 'Web PWA Only', clinician: 'Web + Desktop', school: 'Full Native Fleet (All OS)' },
    { name: 'Dedicated cPanel/Cloud Deployment Option', community: false, family: false, clinician: false, school: true }
  ];

  const faqs = [
    {
      q: 'How does Purchasing Power Parity (PPP) pricing work?',
      a: 'We believe assistive technology and special education tools should be accessible everywhere in the world, regardless of local economic barriers. We automatically apply a 70% PPP discount for users in India and South Asia, while providing specialized NHS and NDIS compliant tiers in the UK and Australia.'
    },
    {
      q: 'Is the Free Community Plan genuinely free forever?',
      a: 'Yes. The Free Community Plan requires no credit card, has no expiration date, and includes the full Core AAC board, switch scanning, tremor filter, and offline PWA capability for individual children and families.'
    },
    {
      q: 'Does AbilityOS replace expensive specialized clinical hardware?',
      a: 'Yes! The Clinician and Institutional tiers include our browser-calibrated Pure-Tone Audiometer (replacing $6,500 diagnostic hardware) and Dr. Speech Studio with real-time FFT spectrum & MPT stopwatch (replacing $3,200 Tiger DRS workstations), using your laptop or tablet microphone and standard headphones.'
    },
    {
      q: 'Can government bodies, CSR programs, or Army schools procure AbilityOS?',
      a: 'Absolutely. We offer dedicated procurement contracts for ASHA schools, DDRCs, District Education Departments, and CSR Equipment Hubs with white-label hosting, localized training, and multi-school analytics.'
    },
    {
      q: 'Do we need to install 93 separate apps on our school or clinic tablets?',
      a: 'No! Unlike fragmented legacy software that forces IT departments to download and juggle dozens of separate apps, AbilityOS delivers a certified, zero-bloat unified architecture. You install ONE master app (or use our instant offline PWA) to access all 31 clinical workstations. You can also pin 1-tap direct shortcuts (e.g., AAC Board or Dr. Speech) to the home screen or lock the tablet into Condition Kiosk Mode.'
    },
    {
      q: 'Can I switch between monthly and annual billing?',
      a: 'Yes, you can upgrade, downgrade, or switch billing cycles at any time. Annual subscriptions include an upfront 25% discount (equivalent to 2 months free).'
    }
  ];

  return (
    <div className="min-h-screen bg-[#f9f6f0] text-[#1c1b1f] selection:bg-[#ffd64e] selection:text-[#1c1b1f] animate-fade-in">
      
      {/* HERO SECTION: Obsidian Dark Header with Ambient Gold Glow */}
      <section className="bg-[#121114] text-white pt-36 pb-24 px-4 sm:px-6 lg:px-8 rounded-b-[44px] relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#4d65ff]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
          
          {/* Top Pill: Auto-Detection Notice */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold backdrop-blur-md">
            <span className="text-base">{currConfig.flag}</span>
            <span className="text-[#ffd64e] font-bold">Auto-Detected:</span>
            <span>{currConfig.countryName} ({currConfig.symbol} {currConfig.code})</span>
            {currConfig.pppDiscountText && (
              <span className="hidden sm:inline text-emerald-400 font-bold ml-1 pl-2 border-l border-white/20">
                {currConfig.pppDiscountText}
              </span>
            )}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-tight text-white">
            Affordable Clinical Care For Everyone. <br className="hidden sm:inline" />
            <span className="text-[#ffd64e]">Transparent, Hardware-Free Pricing.</span>
          </h1>

          <p className="text-slate-300 text-sm sm:text-base max-w-3xl mx-auto font-medium leading-relaxed">
            Replace $15,000+ proprietary therapy hardware and disjointed subscriptions with AbilityOS's unified, accessible clinical operating system.
          </p>

          {/* Interactive Control Bar: Currency Pills & Cadence Switch */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            
            {/* Currency Switcher */}
            <div className="bg-white/10 backdrop-blur-md p-1.5 rounded-full border border-white/15 flex items-center gap-1">
              {(Object.keys(CURRENCIES) as CurrencyCode[]).map((code) => {
                const c = CURRENCIES[code];
                const isActive = currency === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => handleCurrencyChange(code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-extrabold transition-all flex items-center gap-1.5 ${
                      isActive 
                        ? 'bg-[#ffd64e] text-black shadow-md' 
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span>{c.flag}</span>
                    <span>{code}</span>
                  </button>
                );
              })}
            </div>

            {/* Monthly / Annual Toggle */}
            <div className="bg-white/10 backdrop-blur-md p-1.5 rounded-full border border-white/15 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-1.5 rounded-full text-xs font-extrabold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-black shadow-md'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-1.5 rounded-full text-xs font-extrabold transition-all flex items-center gap-1.5 ${
                  billingCycle === 'annual'
                    ? 'bg-[#ffd64e] text-black shadow-md'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>Annual</span>
                <span className="px-2 py-0.5 rounded-full bg-black/20 text-[10px] uppercase font-black tracking-wider">
                  Save 25%
                </span>
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* 4-TIER BENTO PRICING CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* TIER 0: FREE COMMUNITY */}
          <div className="bg-white rounded-[32px] p-7 border border-black/10 shadow-lg flex flex-col justify-between space-y-6 hover:shadow-xl transition-all">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-[#bfecc7] text-[#0f5132]">
                  Free Forever
                </span>
                <span className="text-xs font-bold text-slate-400">Low-Income &amp; Families</span>
              </div>

              <div>
                <h3 className="text-2xl font-black tracking-tight">Community</h3>
                <p className="text-xs text-slate-600 font-medium mt-1 leading-snug">
                  Essential AAC communication board and basic motor adaptations for non-verbal children.
                </p>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <div className="text-4xl font-black tracking-tight">
                  {formatPriceString(0, currency)}
                </div>
                <div className="text-xs text-slate-500 font-medium mt-0.5">
                  No credit card required &bull; Free forever
                </div>
              </div>

              {/* Feature Checklist */}
              <ul className="space-y-2.5 pt-2 text-xs text-slate-700 font-medium">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Core AAC Grid (500+ Vocabulary)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Multilingual TTS (EN, HI, MR)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>CP Switch Scanning &amp; Tremor Dwell</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>1000Hz Quick Hearing Tone Check</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Offline PWA with Local Storage</span>
                </li>
              </ul>
            </div>

            <button
              type="button"
              onClick={() => onSelectPlan ? onSelectPlan('community', billingCycle, currency) : (onNavigateToTab && onNavigateToTab('aac'))}
              className="w-full py-3.5 rounded-full font-black text-xs bg-slate-100 hover:bg-slate-200 text-slate-900 transition flex items-center justify-center gap-2"
            >
              <span>Get Started Free</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* TIER 1: FAMILY PRO */}
          <div className="bg-white rounded-[32px] p-7 border border-black/10 shadow-lg flex flex-col justify-between space-y-6 hover:shadow-xl transition-all">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-[#bbcbf4] text-[#1e3a8a]">
                  Home Therapy
                </span>
                <span className="text-xs font-bold text-slate-400">Parents &amp; Caregivers</span>
              </div>

              <div>
                <h3 className="text-2xl font-black tracking-tight">Family Pro</h3>
                <p className="text-xs text-slate-600 font-medium mt-1 leading-snug">
                  Personalized photo tiles, family voice recordings, and home visual routine schedules.
                </p>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black tracking-tight">
                    {formatPriceString(
                      billingCycle === 'annual' ? pricing.family.annualMonthlyRate : pricing.family.monthly, 
                      currency
                    )}
                  </span>
                  <span className="text-xs text-slate-500 font-semibold">/ month</span>
                </div>
                <div className="text-xs text-slate-500 font-medium mt-0.5">
                  {billingCycle === 'annual' 
                    ? `Billed annually at ${formatPriceString(pricing.family.annualTotal, currency)}/yr`
                    : 'Billed monthly, cancel anytime'
                  }
                </div>
              </div>

              {/* Feature Checklist */}
              <ul className="space-y-2.5 pt-2 text-xs text-slate-700 font-medium">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Everything in Free Community</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Custom Camera Photo Uploads</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Family Voice Recordings per Card</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>First-Then Visual Schedules &amp; Timer</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Voice Balloon Pitch Elevation Game</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Dyslexia Reading Ruler &amp; Contrast</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Sync across 3 Household Devices</span>
                </li>
              </ul>
            </div>

            <button
              type="button"
              onClick={() => onSelectPlan ? onSelectPlan('family', billingCycle, currency) : (onNavigateToTab && onNavigateToTab('aac'))}
              className="w-full py-3.5 rounded-full font-black text-xs bg-slate-900 hover:bg-black text-white shadow transition flex items-center justify-center gap-2"
            >
              <span>Start 14-Day Free Trial</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* TIER 2: CLINICIAN PRO (FEATURED GOLD BORDER) */}
          <div className="bg-[#1c1b1f] text-white rounded-[32px] p-7 border-2 border-[#ffd64e] shadow-2xl flex flex-col justify-between space-y-6 relative hover:scale-[1.02] transition-all">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#ffd64e] text-black font-black text-[11px] uppercase tracking-wider shadow-md">
              Most Popular for SLPs &amp; Clinics
            </div>

            <div className="space-y-4 pt-1">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-[#ffd64e]/20 text-[#ffd64e] border border-[#ffd64e]/40">
                  Full Clinical Suite
                </span>
                <span className="text-xs font-bold text-slate-400">SLPs, OTs &amp; Audiologists</span>
              </div>

              <div>
                <h3 className="text-2xl font-black tracking-tight text-white">Clinician Pro</h3>
                <p className="text-xs text-slate-300 font-medium mt-1 leading-snug">
                  Hardware replacement studio: Dr. Speech FFT waveforms, Pure-Tone Audiometer &amp; IEP generator.
                </p>
              </div>

              <div className="pt-2 border-t border-white/10">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black tracking-tight text-[#ffd64e]">
                    {formatPriceString(
                      billingCycle === 'annual' ? pricing.clinician.annualMonthlyRate : pricing.clinician.monthly, 
                      currency
                    )}
                  </span>
                  <span className="text-xs text-slate-400 font-semibold">/ month</span>
                </div>
                <div className="text-xs text-slate-400 font-medium mt-0.5">
                  {billingCycle === 'annual' 
                    ? `Billed annually at ${formatPriceString(pricing.clinician.annualTotal, currency)}/yr`
                    : 'Billed monthly, cancel anytime'
                  }
                </div>
              </div>

              {/* Feature Checklist */}
              <ul className="space-y-2.5 pt-2 text-xs text-slate-200 font-medium">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span className="font-bold text-white">Dr. Speech Studio (Real-Time FFT &amp; MPT)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span className="font-bold text-white">Diagnostic Pure-Tone Audiometer (125-8000Hz)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span>Speech Banana &amp; PTA Severity Overlay</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span>Articulation Mirror &amp; Oral-Motor Tool</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span>25 Active Patient Caseload Manager</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span>SOAP Notes &amp; 1-Click Official IEP PDF Exporter</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                  <span>Replaces $9,700+ Hardware Workstations</span>
                </li>
              </ul>
            </div>

            <button
              type="button"
              onClick={() => onSelectPlan ? onSelectPlan('clinician', billingCycle, currency) : (onNavigateToTab && onNavigateToTab('disabilities'))}
              className="w-full py-3.5 rounded-full font-black text-xs bg-[#ffd64e] hover:bg-[#f5cb3a] text-black shadow-lg transition flex items-center justify-center gap-2"
            >
              <span>Upgrade to Clinician Pro</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* TIER 3: SCHOOL & NGO INSTITUTIONAL */}
          <div className="bg-white rounded-[32px] p-7 border border-black/10 shadow-lg flex flex-col justify-between space-y-6 hover:shadow-xl transition-all">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-[#f9daf3] text-[#831843]">
                  Unlimited Seats
                </span>
                <span className="text-xs font-bold text-slate-400">Schools &amp; Clinics</span>
              </div>

              <div>
                <h3 className="text-2xl font-black tracking-tight">School &amp; NGO</h3>
                <p className="text-xs text-slate-600 font-medium mt-1 leading-snug">
                  Complete institution ERP: unlimited students, classrooms, EVV GPS, and all 31 modules.
                </p>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black tracking-tight">
                    {formatPriceString(
                      billingCycle === 'annual' ? pricing.school.annualMonthlyRate : pricing.school.monthly, 
                      currency
                    )}
                  </span>
                  <span className="text-xs text-slate-500 font-semibold">/ month</span>
                </div>
                <div className="text-xs text-slate-500 font-medium mt-0.5">
                  {billingCycle === 'annual' 
                    ? `Billed annually at ${formatPriceString(pricing.school.annualTotal, currency)}/yr`
                    : 'Billed monthly, cancel anytime'
                  }
                </div>
              </div>

              {/* Feature Checklist */}
              <ul className="space-y-2.5 pt-2 text-xs text-slate-700 font-medium">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="font-bold text-slate-900">Unlimited Students, Teachers &amp; Therapists</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span className="font-bold text-slate-900">All 31 Clinical Modules Unlocked</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Special Education ERP &amp; Classrooms</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>NDIS &amp; Insurance Billing Claims Engine</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>EVV GPS Geofenced Home Visit Audit</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Unified Fleet Hub: All 31 Modules on Win, Mac & Android</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Priority Tele-Support &amp; Training</span>
                </li>
              </ul>
            </div>

            <button
              type="button"
              onClick={() => onSelectPlan ? onSelectPlan('school', billingCycle, currency) : (onNavigateToTab && onNavigateToTab('school_admin'))}
              className="w-full py-3.5 rounded-full font-black text-xs bg-slate-900 hover:bg-black text-white shadow transition flex items-center justify-center gap-2"
            >
              <span>Provision Organization</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

        </div>
      </section>

      {/* DISTRICT & NGO TCO COST-SAVINGS CALCULATOR */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-[#1c1b1f] text-white rounded-[32px] p-8 sm:p-10 border border-white/15 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-white/10 pb-6">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffd64e]/20 text-[#ffd64e] text-xs font-black uppercase tracking-wider border border-[#ffd64e]/30">
                <Calculator className="w-3.5 h-3.5" /> School TCO Savings Calculator
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                How Much Does Your District Save With AbilityOS?
              </h2>
              <p className="text-xs text-slate-300">
                Compare traditional proprietary hardware procurement against AbilityOS School Institutional licensing.
              </p>
            </div>
            
            <div className="text-right shrink-0">
              <span className="text-[10px] text-slate-400 uppercase font-black tracking-wider block">Estimated Savings</span>
              <span className="text-3xl sm:text-4xl font-black text-[#ffd64e] font-mono">
                {savingsPercent}%
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-8 items-center">
            
            {/* Left: Interactive Slider */}
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-bold text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#ffd64e]" />
                    <span>Number of Special Education Students:</span>
                  </label>
                  <span className="font-mono text-xl font-black text-[#ffd64e] bg-white/10 px-3 py-1 rounded-xl">
                    {studentCount} Students
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="250"
                  step="5"
                  value={studentCount}
                  onChange={(e) => setStudentCount(Number(e.target.value))}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#ffd64e]"
                />
                <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1.5">
                  <span>10 Students (Clinic)</span>
                  <span>100 Students (Special School)</span>
                  <span>250 Students (District Hub)</span>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-xs text-slate-300 space-y-2">
                <div className="font-bold text-white flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span>Hardware-Free Clinical Guarantee:</span>
                </div>
                <p>
                  Zero specialized PC towers, dongles, or calibration cables required. Runs on existing school Chromebooks, iPads, and Android tablets.
                </p>
              </div>
            </div>

            {/* Right: Comparative Cost Breakdown */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-white/10 text-xs">
                <span className="text-slate-400">Traditional Procurement Stack:</span>
                <span className="font-mono font-bold text-red-300 line-through">
                  {formatPriceString(legacyTotalCost, currency)} / yr
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/10 text-xs">
                <span className="text-slate-400">AbilityOS School &amp; NGO License:</span>
                <span className="font-mono font-black text-emerald-400">
                  {formatPriceString(abilityosTotalCost, currency)} / yr
                </span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm font-black text-white">Net Annual District Savings:</span>
                <span className="font-mono text-2xl font-black text-[#ffd64e]">
                  {formatPriceString(totalSavings, currency)}
                </span>
              </div>
            </div>

          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-300">
              Need custom quotation for ASHA Schools, Army Welfare CSR, or State DDRCs?
            </p>
            <button
              type="button"
              onClick={onRequestDistrictDemo || (() => onNavigateToTab && onNavigateToTab('request_demo'))}
              className="px-6 py-3 rounded-full font-black text-xs bg-[#ffd64e] hover:bg-[#f5cb3a] text-black shadow transition flex items-center gap-2 shrink-0"
            >
              <span>Request District Procurement Proposal</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* FULL CLINICAL FEATURE COMPARISON TABLE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-white rounded-[32px] p-8 border border-black/10 shadow-lg space-y-6">
          <div className="text-center space-y-2">
            <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-slate-100 text-slate-700">
              Detailed Specifications
            </span>
            <h2 className="text-3xl font-black text-[#1c1b1f] tracking-tight">
              Full 31-Module Clinical Feature Comparison
            </h2>
            <p className="text-xs text-slate-500 max-w-xl mx-auto">
              Compare clinical capabilities, diagnostic tools, and administrative entitlements across our tiers.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="py-4 px-4 font-black text-slate-900 w-2/5">Capability / Feature</th>
                  <th className="py-4 px-3 font-black text-slate-700 text-center">Free Community</th>
                  <th className="py-4 px-3 font-black text-slate-700 text-center">Family Pro</th>
                  <th className="py-4 px-3 font-black text-[#1c1b1f] bg-[#ffd64e]/20 text-center rounded-t-xl">
                    Clinician Pro
                  </th>
                  <th className="py-4 px-3 font-black text-slate-700 text-center">School &amp; NGO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {comparisonFeatures.map((feat, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/80 transition">
                    <td className="py-3 px-4 font-bold text-slate-800">{feat.name}</td>
                    
                    <td className="py-3 px-3 text-center">
                      {typeof feat.community === 'boolean' ? (
                        feat.community ? (
                          <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                        ) : (
                          <span className="text-slate-300">&mdash;</span>
                        )
                      ) : (
                        <span className="font-semibold text-slate-600">{feat.community}</span>
                      )}
                    </td>

                    <td className="py-3 px-3 text-center">
                      {typeof feat.family === 'boolean' ? (
                        feat.family ? (
                          <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                        ) : (
                          <span className="text-slate-300">&mdash;</span>
                        )
                      ) : (
                        <span className="font-semibold text-slate-600">{feat.family}</span>
                      )}
                    </td>

                    <td className="py-3 px-3 text-center bg-[#ffd64e]/10 font-bold">
                      {typeof feat.clinician === 'boolean' ? (
                        feat.clinician ? (
                          <Check className="w-4 h-4 text-slate-900 mx-auto" />
                        ) : (
                          <span className="text-slate-300">&mdash;</span>
                        )
                      ) : (
                        <span className="font-bold text-slate-900">{feat.clinician}</span>
                      )}
                    </td>

                    <td className="py-3 px-3 text-center">
                      {typeof feat.school === 'boolean' ? (
                        feat.school ? (
                          <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                        ) : (
                          <span className="text-slate-300">&mdash;</span>
                        )
                      ) : (
                        <span className="font-bold text-blue-700">{feat.school}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
        <div className="bg-white rounded-[32px] p-8 border border-black/10 shadow-lg space-y-6">
          <div className="text-center space-y-2">
            <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-slate-100 text-slate-700">
              Clarity &amp; Compliance
            </span>
            <h2 className="text-3xl font-black text-[#1c1b1f] tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3 pt-4">
            {faqs.map((faq, idx) => {
              const isExpanded = expandedFaq === idx;
              return (
                <div 
                  key={idx} 
                  className="rounded-2xl border border-slate-200 overflow-hidden transition"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                    className="w-full p-4 text-left font-bold text-sm text-slate-900 flex items-center justify-between hover:bg-slate-50"
                  >
                    <span>{faq.q}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </button>
                  {isExpanded && (
                    <div className="p-4 pt-0 text-xs text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
};
